import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";

interface EmailGateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EmailGateModal({ isOpen, onClose }: EmailGateModalProps) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [notFound, setNotFound] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError("");
    setNotFound(false);

    try {
      const { data, error: rpcError } = await supabase
        .rpc("check_member_email_exists", { email_input: email.trim() });

      if (rpcError) {
        setError("Erro ao verificar o email. Tente novamente.");
        setLoading(false);
        return;
      }

      if (!data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
      const pathPrefix = basePath ? `/${basePath}` : "";

      const { error: authError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}${pathPrefix}/meu-perfil`,
        },
      });

      if (authError) {
        setError(authError.message || "Erro ao enviar o link de acesso. Tente novamente.");
        setLoading(false);
        return;
      }

      localStorage.setItem("entre-elas-email", email.trim());
      setMagicLinkSent(true);
      setLoading(false);
    } catch {
      setError("Erro inesperado. Tente novamente.");
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setError("");
    setLoading(false);
    setMagicLinkSent(false);
    setNotFound(false);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-6"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-md bg-white rounded-2xl p-8 md:p-10 shadow-sm animate-fade-in-up">
        {magicLinkSent ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 mx-auto mb-6">
              <i className="ri-mail-send-line text-2xl text-green-500" />
            </div>
            <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
              Verifique seu email
            </h2>
            <p className="font-body text-sm text-brown/70 leading-relaxed mb-2">
              Enviamos um link de acesso para <strong>{email}</strong>.
            </p>
            <p className="font-body text-xs text-brown/50 leading-relaxed mb-6">
              Clique no link que chegou no seu email para acessar seu perfil. Se não encontrar, verifique a pasta de spam.
            </p>
            <button
              onClick={() => {
                setMagicLinkSent(false);
                setError("");
              }}
              className="font-body text-sm text-primary hover:text-primary-dark transition-colors underline cursor-pointer"
            >
              Usar outro email
            </button>
            <button
              onClick={handleClose}
              className="block mx-auto mt-3 font-body text-xs text-brown/40 hover:text-brown/60 transition-colors underline cursor-pointer"
            >
              Fechar
            </button>
          </div>
        ) : notFound ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-50 mx-auto mb-6">
              <i className="ri-user-search-line text-2xl text-amber-500" />
            </div>
            <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
              Você ainda não faz parte da comunidade
            </h2>
            <p className="font-body text-sm text-brown/60 leading-relaxed mb-6">
              O email <strong>{email}</strong> não foi encontrado em nossos cadastros. Que tal se juntar a nós?
            </p>
            <Link
              to="/cadastro"
              onClick={handleClose}
              className="inline-block font-body text-sm px-8 py-3.5 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark whitespace-nowrap"
            >
              Quero me cadastrar
            </Link>
            <button
              onClick={() => setNotFound(false)}
              className="block mx-auto mt-4 font-body text-xs text-brown/40 hover:text-brown/60 transition-colors underline cursor-pointer"
            >
              Tentar outro email
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary/10 mx-auto mb-4">
                <i className="ri-user-line text-2xl text-primary" />
              </div>
              <h2 className="font-display text-xl md:text-2xl text-brown mb-2">
                Acessar meu perfil
              </h2>
              <p className="font-body text-sm text-brown/60">
                Digite o email usado no cadastro para receber um link de acesso.
              </p>
            </div>

            {error && (
              <div className="mb-5 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 font-body text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="font-body text-sm text-brown block mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  placeholder="seu@email.com"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full font-body text-sm px-6 py-3.5 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    Verificando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-mail-send-line" />
                    Receber link de acesso
                  </span>
                )}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-nude text-center">
              <p className="font-body text-xs text-brown/50 mb-3">
                Ainda não faz parte da comunidade?
              </p>
              <Link
                to="/cadastro"
                onClick={handleClose}
                className="inline-block font-body text-sm px-6 py-2.5 rounded-full border border-primary text-primary tracking-wide uppercase transition-all duration-300 hover:bg-primary hover:text-white whitespace-nowrap"
              >
                Quero me cadastrar
              </Link>
            </div>

            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-brown/30 hover:text-brown/60 hover:bg-brown/5 transition-colors cursor-pointer"
              aria-label="Fechar"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}